import React, { useEffect, useState } from "react";
import {
    Box,
    VStack,
    HStack,
    Text,
    Button,
    Input,
    FormControl,
    FormLabel,
    useColorModeValue,
    useToast,
    Alert,
    AlertIcon,
    AlertDescription,
    IconButton,
    InputGroup,
    InputRightElement,
    Divider,
    Switch,
    Stack,
    SimpleGrid,
    Icon,
    Badge,
    Heading
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import PageShell from "../layout/PageShell";
import { FiSettings, FiKey, FiTruck, FiShield, FiZap, FiCopy, FiGlobe } from "react-icons/fi";

const LIVRO_NUAGE_CONFIG_COLLECTION = "livroNuageConfig";
const LIVRO_NUAGE_CONFIG_DOC_ID = "global";

interface Credentials {
    customerId: string;
    clientId: string;
    clientSecret: string;
    webhookSigningKey: string;
}

interface LivroNuageConfig {
    sandbox?: Partial<Credentials>;
    production?: Partial<Credentials>;
    testMode?: boolean;
    active?: boolean;
    lastUpdated?: any;
    updatedBy?: string;
}

const LivroNuageSettings: React.FC = () => {
    const toast = useToast();
    const { currentUser } = useAuth();

    const initialCreds: Credentials = { customerId: "", clientId: "", clientSecret: "", webhookSigningKey: "" };
    const [sandbox, setSandbox] = useState<Credentials>(initialCreds);
    const [production, setProduction] = useState<Credentials>(initialCreds);
    const [testMode, setTestMode] = useState(true);
    const [active, setActive] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [showSandboxSecret, setShowSandboxSecret] = useState(false);
    const [showProdSecret, setShowProdSecret] = useState(false);

    const bgPage = useColorModeValue("gray.50", "gray.900");
    const bgCard = useColorModeValue("white", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const textColor = useColorModeValue("gray.700", "white");
    const textSecondary = useColorModeValue("gray.500", "gray.400");

    useEffect(() => {
        const loadConfig = async () => {
            try {
                const docRef = doc(db, LIVRO_NUAGE_CONFIG_COLLECTION, LIVRO_NUAGE_CONFIG_DOC_ID);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data() as LivroNuageConfig;
                    if (data.sandbox) setSandbox({ ...initialCreds, ...data.sandbox });
                    if (data.production) setProduction({ ...initialCreds, ...data.production });
                    setTestMode(data.testMode !== undefined ? data.testMode : true);
                    setActive(data.active || false);
                }
            } catch (error) {
                console.error("Error loading Livro Nuage config:", error);
            }
        };

        loadConfig();
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const updateData: LivroNuageConfig = {
                sandbox,
                production,
                testMode,
                active,
                lastUpdated: serverTimestamp(),
                updatedBy: currentUser?.email || "unknown",
            };

            const configDocRef = doc(db, LIVRO_NUAGE_CONFIG_COLLECTION, LIVRO_NUAGE_CONFIG_DOC_ID);
            await setDoc(configDocRef, updateData, { merge: true });

            toast({
                title: "Success",
                description: "Livro Nuage settings saved successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error: any) {
            console.error("Error saving Livro Nuage config:", error);
            toast({
                title: "Error",
                description: error.message || "Failed to save Livro Nuage settings",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <PageShell
            title="Livro Nuage Settings"
            description="Project-wide Livro Nuage configuration for sandbox and production."
            wide
            actions={
                <Button onClick={handleSave} isLoading={isSaving} loadingText="Saving...">
                    Save Global Config
                </Button>
            }
        >
            <VStack spacing={6} align="stretch">

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {/* Control Card */}
                    <Box p={6} bg={bgCard} borderRadius="2xl" border="1px" borderColor={borderColor} boxShadow="sm" gridColumn={{ md: "span 3" }}>
                        <HStack justify="space-between">
                            <HStack spacing={10}>
                                <FormControl display="flex" alignItems="center">
                                    <Box mr={4}>
                                        <FormLabel mb="0" fontWeight="bold">Global Integration Status</FormLabel>
                                        <Text fontSize="xs" color={textSecondary}>Enable/Disable Livro Nuage ecosystem</Text>
                                    </Box>
                                    <Switch
                                        isChecked={active}
                                        onChange={(e) => setActive(e.target.checked)}
                                        colorScheme="green"
                                        size="lg"
                                    />
                                </FormControl>

                                <Divider orientation="vertical" h="40px" />

                                <FormControl display="flex" alignItems="center">
                                    <Box mr={4}>
                                        <FormLabel mb="0" fontWeight="bold">Operation Mode</FormLabel>
                                        <Text fontSize="xs" color={textSecondary}>Switch between Sandbox and Production</Text>
                                    </Box>
                                    <HStack spacing={3}>
                                        <Badge colorScheme={!testMode ? "gray" : "orange"} variant={!testMode ? "outline" : "solid"}>SANDBOX</Badge>
                                        <Switch
                                            isChecked={!testMode}
                                            onChange={(e) => setTestMode(!e.target.checked)}
                                            colorScheme="blue"
                                            size="lg"
                                        />
                                        <Badge colorScheme={!testMode ? "green" : "gray"} variant={!testMode ? "solid" : "outline"}>PRODUCTION</Badge>
                                    </HStack>
                                </FormControl>
                            </HStack>

                            {active && (
                                <Badge p={2} borderRadius="md" colorScheme={testMode ? "orange" : "green"}>
                                    LIVE MODE: {testMode ? "SANDBOX" : "PRODUCTION"}
                                </Badge>
                            )}
                        </HStack>
                    </Box>

                    {/* Sandbox Credentials */}
                    <Box p={6} bg={bgCard} borderRadius="2xl" border="1px" borderColor={borderColor} boxShadow="sm">
                        <HStack mb={6}>
                            <Icon as={FiShield as any} color="orange.400" w={5} h={5} />
                            <Text fontWeight="bold" fontSize="lg">Sandbox Credentials</Text>
                        </HStack>
                        <Stack spacing={4}>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">Customer ID</FormLabel>
                                <Input
                                    value={sandbox.customerId}
                                    onChange={(e) => setSandbox({ ...sandbox, customerId: e.target.value })}
                                    placeholder="Enter Sandbox Customer ID"
                                    bg={bgPage}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">Client ID</FormLabel>
                                <Input
                                    value={sandbox.clientId}
                                    onChange={(e) => setSandbox({ ...sandbox, clientId: e.target.value })}
                                    placeholder="Enter Sandbox Client ID"
                                    bg={bgPage}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">Client Secret</FormLabel>
                                <InputGroup>
                                    <Input
                                        type={showSandboxSecret ? "text" : "password"}
                                        value={sandbox.clientSecret}
                                        onChange={(e) => setSandbox({ ...sandbox, clientSecret: e.target.value })}
                                        placeholder="Enter Sandbox Secret"
                                        bg={bgPage}
                                    />
                                    <InputRightElement>
                                        <IconButton
                                            aria-label="Toggle secret"
                                            icon={showSandboxSecret ? <ViewOffIcon /> : <ViewIcon />}
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setShowSandboxSecret(!showSandboxSecret)}
                                        />
                                    </InputRightElement>
                                </InputGroup>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">Webhook Signing Key</FormLabel>
                                <Input
                                    value={sandbox.webhookSigningKey}
                                    onChange={(e) => setSandbox({ ...sandbox, webhookSigningKey: e.target.value })}
                                    placeholder="Enter Sandbox Webhook Key"
                                    bg={bgPage}
                                />
                            </FormControl>
                        </Stack>
                    </Box>

                    {/* Production Credentials */}
                    <Box p={6} bg={bgCard} borderRadius="2xl" border="1px" borderColor={borderColor} boxShadow="sm">
                        <HStack mb={6}>
                            <Icon as={FiZap as any} color="green.400" w={5} h={5} />
                            <Text fontWeight="bold" fontSize="lg">Production Credentials</Text>
                        </HStack>
                        <Stack spacing={4}>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">Customer ID</FormLabel>
                                <Input
                                    value={production.customerId}
                                    onChange={(e) => setProduction({ ...production, customerId: e.target.value })}
                                    placeholder="Enter Production Customer ID"
                                    bg={bgPage}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">Client ID</FormLabel>
                                <Input
                                    value={production.clientId}
                                    onChange={(e) => setProduction({ ...production, clientId: e.target.value })}
                                    placeholder="Enter Production Client ID"
                                    bg={bgPage}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">Client Secret</FormLabel>
                                <InputGroup>
                                    <Input
                                        type={showProdSecret ? "text" : "password"}
                                        value={production.clientSecret}
                                        onChange={(e) => setProduction({ ...production, clientSecret: e.target.value })}
                                        placeholder="Enter Production Secret"
                                        bg={bgPage}
                                    />
                                    <InputRightElement>
                                        <IconButton
                                            aria-label="Toggle secret"
                                            icon={showProdSecret ? <ViewOffIcon /> : <ViewIcon />}
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setShowProdSecret(!showProdSecret)}
                                        />
                                    </InputRightElement>
                                </InputGroup>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">Webhook Signing Key</FormLabel>
                                <Input
                                    value={production.webhookSigningKey}
                                    onChange={(e) => setProduction({ ...production, webhookSigningKey: e.target.value })}
                                    placeholder="Enter Production Webhook Key"
                                    bg={bgPage}
                                />
                            </FormControl>
                        </Stack>
                    </Box>

                    {/* Webhook Configuration */}
                    <Box p={6} bg={bgCard} borderRadius="2xl" border="1px" borderColor={borderColor} boxShadow="sm" gridColumn={{ md: "span 2" }}>
                        <HStack mb={4} justify="space-between">
                            <HStack>
                                <Icon as={FiGlobe as any} color="blue.500" w={5} h={5} />
                                <Text fontWeight="bold" fontSize="lg">Webhook Configuration</Text>
                            </HStack>
                            <Badge colorScheme="blue" variant="subtle">Firebase Cloud Function</Badge>
                        </HStack>

                        <VStack align="start" spacing={4}>
                            <Text fontSize="sm" color={textSecondary}>
                                Use this URL in the Uber Developer Dashboard to receive real-time delivery status updates.
                            </Text>

                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">Webhook Endpoint URL</FormLabel>
                                <InputGroup size="md">
                                    <Input
                                        pr="4.5rem"
                                        readOnly
                                        value={`https://northamerica-northeast1-fiverrrms1.cloudfunctions.net/uberDirectWebhook`}
                                        bg={bgPage}
                                        fontSize="sm"
                                    />
                                    <InputRightElement width="4.5rem">
                                        <Button
                                            h="1.75rem"
                                            size="sm"
                                            onClick={() => {
                                                navigator.clipboard.writeText(`https://northamerica-northeast1-fiverrrms1.cloudfunctions.net/uberDirectWebhook`);
                                                toast({
                                                    title: "URL Copied",
                                                    status: "success",
                                                    duration: 2000,
                                                });
                                            }}
                                            leftIcon={<Icon as={FiCopy as any} />}
                                        >
                                            Copy
                                        </Button>
                                    </InputRightElement>
                                </InputGroup>
                            </FormControl>

                            <Alert status="info" borderRadius="xl" fontSize="sm">
                                <AlertIcon />
                                <Box>
                                    <AlertDescription display="block">
                                        After pasting this URL into Uber, copy the <strong>Webhook Signing Key</strong> provided by Uber back into the credential sections above to enable secure signature verification.
                                    </AlertDescription>
                                </Box>
                            </Alert>
                        </VStack>
                    </Box>

                    {/* Info Card */}
                    <Box p={6} bg="gray.800" borderRadius="2xl" color="white" boxShadow="xl" gridColumn={{ base: "span 1" }}>
                        <VStack align="start" spacing={4}>
                            <Icon as={FiTruck as any} boxSize={8} color="blue.400" />
                            <Text fontWeight="bold" fontSize="xl">Deployment Guide</Text>
                            <Stack spacing={3} fontSize="sm">
                                <Text opacity={0.9}>
                                    1. <strong>Global Setup</strong>: Set your Client ID and Secret in both Sandbox and Production.
                                </Text>
                                <Text opacity={0.9}>
                                    2. <strong>Webhook</strong>: Copy the URL above to Uber's dashboard and retrieve your signing keys.
                                </Text>
                                <Text opacity={0.9}>
                                    3. <strong>Store Settings</strong>: Individual stores must activate "Uber" in their specific store settings to start receiving orders.
                                </Text>
                            </Stack>
                        </VStack>
                    </Box>
                </SimpleGrid>
            </VStack>
        </PageShell>
    );
};

export default LivroNuageSettings;
